import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { RsjInstructionPaymentOpinion } from '../../../../../shared/models/enums/rsj-instruction-payment-opinion.enum';
import { RsjInstructionPaymentDto } from '../../dto/rsj-instruction-payment.dto';
import { RsjInstructionVisibility } from '../../dto/rsj-instruction-visibility.enum';

@Component({
  selector: 'app-rsj-instruction-payment',
  templateUrl: './rsj-instruction-payment.component.html',
})
export class RsjInstructionPaymentComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject();

  public paymentForm: FormGroup<{
    paymentAmount: FormControl<number | null>;
    paymentDuration: FormControl<number | null>;
    paymentOpinion: FormControl<string | null>;
    paymentComment: FormControl<string | null>;
    paymentCounterProposal: FormControl<boolean | null>;
    paymentCounterAmount: FormControl<number | null>;
    paymentCounterDuration: FormControl<number | null>;
    paymentCounterComment: FormControl<string | null>;
  }>;
  public updated: boolean = false;
  public submitted: boolean = false;

  public requiredComment: boolean = false;

  public RsjInstructionPaymentOpinion = RsjInstructionPaymentOpinion;

  @Input() public rsjPayment: RsjInstructionPaymentDto;
  @Input() public visibility: RsjInstructionVisibility;

  public ngOnInit(): void {
    this.initForm();
    this.initValueChangeHandlers();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): boolean {
    if (this.updated) {
      this.submitted = true;
    }
    return !this.updated || this.paymentForm.valid;
  }

  public getDto(): RsjInstructionPaymentDto {
    if (this.paymentForm.valid) {
      this.rsjPayment = {
        paymentAmount: this.paymentForm.controls.paymentAmount.value,
        paymentDuration: this.paymentForm.controls.paymentDuration.value,
        paymentOpinion: this.paymentForm.controls.paymentOpinion.value,
        paymentComment: this.paymentForm.controls.paymentComment.value,
        paymentCounterProposal: this.paymentForm.controls.paymentCounterProposal.value,
        paymentCounterAmount: this.paymentForm.controls.paymentCounterAmount.value,
        paymentCounterDuration: this.paymentForm.controls.paymentCounterDuration.value,
        paymentCounterComment: this.paymentForm.controls.paymentCounterComment.value,
      } as RsjInstructionPaymentDto;
    }
    return this.rsjPayment;
  }

  private initForm(): void {
    this.paymentForm = new FormGroup({
      paymentAmount: new FormControl(this.rsjPayment?.paymentAmount, [Validators.required]),
      paymentDuration: new FormControl(this.rsjPayment?.paymentDuration, [Validators.required]),
      paymentOpinion: new FormControl(this.rsjPayment?.paymentOpinion, [Validators.required]),
      paymentComment: new FormControl(this.rsjPayment?.paymentComment),
      paymentCounterProposal: new FormControl({
        value: this.rsjPayment?.paymentCounterProposal,
        disabled: this.visibility !== RsjInstructionVisibility.DIE_PAYMENTS,
      }),
      paymentCounterAmount: new FormControl(this.rsjPayment?.paymentCounterAmount, [Validators.required]),
      paymentCounterDuration: new FormControl(this.rsjPayment?.paymentCounterDuration, [Validators.required]),
      paymentCounterComment: new FormControl(this.rsjPayment?.paymentCounterComment, [Validators.required]),
    });
    this.updateCommentFieldValidators();
    this.updateCounterProposalFieldsVisibility();
    if (this.visibility !== RsjInstructionVisibility.FULL && this.visibility !== RsjInstructionVisibility.DIE_PAYMENTS) {
      this.paymentForm.disable({ emitEvent: false });
    }
  }

  private initValueChangeHandlers(): void {
    this.paymentForm.controls.paymentOpinion.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateCommentFieldValidators());
    this.paymentForm.controls.paymentCounterProposal.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateCounterProposalFieldsVisibility());
    this.paymentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => (this.updated = true));
  }

  private updateCommentFieldValidators(): void {
    if (
      [RsjInstructionPaymentOpinion.Negative, RsjInstructionPaymentOpinion.Neutral].includes(
        this.paymentForm.controls.paymentOpinion.value as RsjInstructionPaymentOpinion,
      )
    ) {
      this.requiredComment = true;
      this.paymentForm.controls.paymentComment.setValidators([Validators.required]);
    } else {
      this.requiredComment = false;
      this.paymentForm.controls.paymentComment.setValidators([]);
    }
    this.paymentForm.controls.paymentComment.updateValueAndValidity();
  }

  private updateCounterProposalFieldsVisibility(): void {
    if (this.visibility !== RsjInstructionVisibility.DIE_PAYMENTS) {
      this.paymentForm.controls.paymentCounterAmount.disable();
      this.paymentForm.controls.paymentCounterDuration.disable();
      this.paymentForm.controls.paymentCounterComment.disable();
    } else if (this.paymentForm.controls.paymentCounterProposal.value === true) {
      this.paymentForm.controls.paymentCounterAmount.enable();
      this.paymentForm.controls.paymentCounterDuration.enable();
      this.paymentForm.controls.paymentCounterComment.enable();
      this.paymentForm.controls.paymentCounterAmount.updateValueAndValidity();
      this.paymentForm.controls.paymentCounterDuration.updateValueAndValidity();
      this.paymentForm.controls.paymentCounterComment.updateValueAndValidity();
    } else {
      this.paymentForm.controls.paymentCounterAmount.reset();
      this.paymentForm.controls.paymentCounterAmount.disable();
      this.paymentForm.controls.paymentCounterDuration.reset();
      this.paymentForm.controls.paymentCounterDuration.disable();
      this.paymentForm.controls.paymentCounterComment.reset();
      this.paymentForm.controls.paymentCounterComment.disable();
    }
    if (this.visibility === RsjInstructionVisibility.DIE_PAYMENTS) {
      this.paymentForm.controls.paymentAmount.disable();
      this.paymentForm.controls.paymentDuration.disable();
      this.paymentForm.controls.paymentOpinion.disable();
      this.paymentForm.controls.paymentComment.disable();
    }
  }
}
